/**
 * Utility functions for path smoothing and simplification
 */

/**
 * Simplifies a path using the Ramer-Douglas-Peucker algorithm
 * Reduces the number of points in a path while maintaining its shape
 * 
 * @param {Array} points - Array of coordinate points [[lat1, lng1], [lat2, lng2], ...]
 * @param {Number} tolerance - Higher tolerance means more simplification
 * @returns {Array} Simplified array of points
 */
export function simplifyPath(points, tolerance = 0.0001) {
  if (points.length <= 2) {
    return points;
  }
  
  // Find the point with the maximum distance from line between start and end
  const findMaxDistance = (points, start, end) => {
    const startPoint = points[start];
    const endPoint = points[end];
    
    let maxDistance = 0;
    let maxIndex = 0;
    
    for (let i = start + 1; i < end; i++) {
      const distance = perpendicularDistance(points[i], startPoint, endPoint);
      if (distance > maxDistance) {
        maxDistance = distance;
        maxIndex = i;
      }
    }
    
    return { maxDistance, maxIndex };
  };
  
  // Calculate perpendicular distance from a point to a line
  const perpendicularDistance = (point, lineStart, lineEnd) => {
    const [lat, lng] = point;
    const [lat1, lng1] = lineStart;
    const [lat2, lng2] = lineEnd;
    
    // Convert to radians for proper distance calculation on Earth surface
    const latRad = lat * Math.PI / 180;
    const lat1Rad = lat1 * Math.PI / 180;
    const lat2Rad = lat2 * Math.PI / 180;
    const lngRad = lng * Math.PI / 180;
    const lng1Rad = lng1 * Math.PI / 180;
    const lng2Rad = lng2 * Math.PI / 180;
    
    // For simplicity, we'll use planar distance for the algorithm
    // This is an approximation that works well enough for small distances
    const x = (lng2Rad - lng1Rad) * Math.cos((lat1Rad + lat2Rad) / 2);
    const y = lat2Rad - lat1Rad;
    const denominator = Math.sqrt(x * x + y * y);
    
    if (denominator === 0) {
      return 0;
    }
    
    const x1 = (lngRad - lng1Rad) * Math.cos((lat1Rad + latRad) / 2);
    const y1 = latRad - lat1Rad;
    
    // Perpendicular distance calculation
    const numerator = Math.abs(y * x1 - x * y1);
    const earthRadius = 6371; // Earth radius in km
    
    return (numerator / denominator) * earthRadius;
  };
  
  // Recursive implementation of the Douglas-Peucker algorithm
  const douglasPeucker = (points, startIndex, endIndex, tolerance) => {
    if (endIndex <= startIndex + 1) {
      return [];
    }
    
    const { maxDistance, maxIndex } = findMaxDistance(points, startIndex, endIndex);
    
    if (maxDistance > tolerance) {
      // Recursively simplify both segments
      const firstSegment = douglasPeucker(points, startIndex, maxIndex, tolerance);
      const secondSegment = douglasPeucker(points, maxIndex, endIndex, tolerance);
      
      // Combine the results and include the max-distance point
      return [...firstSegment, points[maxIndex], ...secondSegment];
    }
    
    // If max distance is within tolerance, remove all points between start and end
    return [];
  };
  
  // Start the algorithm
  const result = douglasPeucker(points, 0, points.length - 1, tolerance);
  
  // Make sure to include the first and last points
  return [points[0], ...result, points[points.length - 1]];
}

/**
 * Creates ultra-smooth curves using Catmull-Rom splines
 * This produces genuinely smooth, flowing lines without pixelation
 * 
 * @param {Array} points - Array of coordinate points [[lat, lng], ...]
 * @param {Number} tension - Curve tension (0.5 is standard Catmull-Rom)
 * @param {Number} segments - Number of interpolated points between each pair
 * @returns {Array} High-density smooth curve points
 */
function createCatmullRomSpline(points, tension = 0.5, segments = 20) {
  if (points.length < 2) return points;
  
  const result = [];
  
  // Add the first point
  result.push(points[0]);
  
  // Generate smooth curve between each pair of points
  for (let i = 0; i < points.length - 1; i++) {
    // Get the 4 control points for Catmull-Rom spline
    const p0 = i > 0 ? points[i - 1] : points[0];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = i < points.length - 2 ? points[i + 2] : points[points.length - 1];
    
    // Calculate the distance between p1 and p2 to adapt segment count
    const distance = calculateDistance(p1, p2);
    
    // Adaptive segment count based on distance
    // Closer points need fewer segments, distant points need more
    const adaptiveSegments = Math.max(5, Math.min(segments, Math.floor(distance * 1000)));
    
    // Create interpolated points between p1 and p2
    for (let t = 1; t <= adaptiveSegments; t++) {
      const u = t / adaptiveSegments;
      const interpolated = catmullRomInterpolate(p0, p1, p2, p3, u, tension);
      result.push(interpolated);
    }
  }
  
  return result;
}

/**
 * Catmull-Rom spline interpolation for a single point
 * This creates smooth curves that pass through all control points
 */
function catmullRomInterpolate(p0, p1, p2, p3, t, tension) {
  const t2 = t * t;
  const t3 = t2 * t;
  
  // Catmull-Rom basis functions
  const b0 = -tension * t3 + 2 * tension * t2 - tension * t;
  const b1 = (2 - tension) * t3 + (tension - 3) * t2 + 1;
  const b2 = (tension - 2) * t3 + (3 - 2 * tension) * t2 + tension * t;
  const b3 = tension * t3 - tension * t2;
  
  // Interpolate coordinates
  const lat = b0 * p0[0] + b1 * p1[0] + b2 * p2[0] + b3 * p3[0];
  const lng = b0 * p0[1] + b1 * p1[1] + b2 * p2[1] + b3 * p3[1];
  
  return [lat, lng];
}

/**
 * Calculate approximate distance between two geographic points
 * Used for adaptive interpolation
 */
function calculateDistance(point1, point2) {
  const [lat1, lng1] = point1;
  const [lat2, lng2] = point2;
  
  const latDiff = lat2 - lat1;
  const lngDiff = lng2 - lng1;
  
  return Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);
}

/**
 * Alternative ultra-smooth path using B-spline approximation
 * This creates even smoother curves for highly detailed paths
 */
export function createUltraSmoothPath(coordinates) {
  if (coordinates.length <= 3) {
    return coordinates;
  }
  
  // First pass: aggressive simplification
  const simplified = simplifyPath(coordinates, 0.0005);
  
  // Second pass: Catmull-Rom with high density
  const splined = createCatmullRomSpline(simplified, 0.3, 25);
  
  // Third pass: light smoothing to remove any remaining artifacts
  return lightSmooth(splined);
}

/**
 * Light smoothing pass to remove final artifacts
 * Uses simple averaging with immediate neighbors
 */
function lightSmooth(points) {
  if (points.length < 3) return points;
  
  const result = [points[0]]; // Keep first point unchanged
  
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];
    
    // Simple weighted average
    const smoothed = [
      (prev[0] * 0.25 + curr[0] * 0.5 + next[0] * 0.25),
      (prev[1] * 0.25 + curr[1] * 0.5 + next[1] * 0.25)
    ];
    
    result.push(smoothed);
  }
  
  result.push(points[points.length - 1]); // Keep last point unchanged
  
  return result;
} 